/**
 * CenterMessage - Full-screen centered hero banner overlay. Title 48px bold,
 * optional subtitle 18px muted. Fade-in 0.4s. Used for "Victory", "Defeated",
 * round announcements, and similar dramatic moments.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface CenterMessageShowOptions {
  /** Main heading text (e.g. "Victory", "Defeated"). */
  title: string;
  /** Optional supporting text shown beneath the title. */
  subtitle?: string;
  /** CSS colour override for the title (e.g. "#4ade80" for victory green). */
  color?: string;
  /**
   * Auto-hide delay in ms.
   * 0 (default) = stay visible until `hide()` is called.
   */
  duration?: number;
}

export interface CenterMessageOptions extends BaseUIComponentOptions {}

export class CenterMessage extends BaseUIComponent<CenterMessageOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Center Message',
    category: 'hud',
    summary: 'Full-screen centered hero banner with 48px title and optional subtitle. Fade-in 0.4s.',
    useCases: ['victory', 'defeat', 'round start', 'round end', 'announcement', 'overlay', 'banner'],
    optionsType: 'CenterMessageOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/center-message.html',
      styles: '@engine/assets/ui/styles/center-message.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private subtitleElement: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: CenterMessage.metadata.assetPaths.template,
      stylesPath: CenterMessage.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<CenterMessageOptions> {
    return {
      position: 'center',
      visible: false,
      customClasses: [],
      customStyles: {},
    };
  }

  protected override getInitialData(): Record<string, any> {
    return { title: '', subtitle: '' };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-center-message') as HTMLElement | null;
    this.titleElement = this.layout.querySelector('[data-center-message-title]') as HTMLElement | null;
    this.subtitleElement = this.layout.querySelector('[data-center-message-subtitle]') as HTMLElement | null;
  }

  /**
   * Reveal the overlay with the provided content. If `duration` > 0 the overlay
   * hides automatically after that many ms.
   */
  public override show(opts?: CenterMessageShowOptions): void {
    if (!opts) return;
    this.clearHideTimer();
    const { title, subtitle = '', color, duration = 0 } = opts;

    if (this.titleElement) {
      this.titleElement.textContent = title;
      this.titleElement.style.color = color ?? '';
    }
    if (this.subtitleElement) this.subtitleElement.textContent = subtitle;
    if (this.rootElement) this.rootElement.dataset.visible = 'true';
    this.isVisible = true;

    if (duration > 0) {
      this.hideTimer = setTimeout(() => this.hide(), duration);
    }
  }

  /** Fade the overlay out. */
  public override hide(): void {
    this.clearHideTimer();
    if (this.rootElement) this.rootElement.dataset.visible = 'false';
    this.isVisible = false;
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  protected override onDestroy(): void {
    this.clearHideTimer();
    this.rootElement = null;
    this.titleElement = null;
    this.subtitleElement = null;
  }
}
